import React from 'react';

import getThemeLookup from '../../selectors/getThemeLookup';
import combineStyles from '../../utils/combineStyles';
import { useAppSelector } from '../../app/hooks';
import { Show } from '../atoms/Show';
import Text from '../Text';
import { CONTAINER_PROPERTIES, TEXT_PROPERTIES } from '../../utils/theme';
import Pseudo from '../atoms/Pseudo';

interface ChipProps {
  value?: string;
  themeKey?: string;
  icon?: React.ReactNode;
  wrap?: boolean;
  lines?: number;
}

/**
 * # Chip
 *
 * Chips are utilised to present compact, contextual options or information
 * that relate to the user’s current task.
 *
 * * For more information: https://www.figma.com/design/jOaTwFaOJyehR2FCBOfq37/Redcat-Design-System?node-id=99-3642
 *
 */
const Chip = ({ value, themeKey, icon, lines }: ChipProps) => {
  const p = useAppSelector(getThemeLookup);

  return (
    <Show when={!!value}>
      <Pseudo>
        {({ isHover, isActive }) => (
          <div
            style={combineStyles(
              styles.chip,

              // Base
              p('chip', CONTAINER_PROPERTIES),
              isHover && p('chipHover', CONTAINER_PROPERTIES),
              isActive && p('chipActive', CONTAINER_PROPERTIES),

              // Themed overrides
              !!themeKey &&
                combineStyles(
                  p(themeKey, CONTAINER_PROPERTIES),
                  isHover && p(`${themeKey}Hovered`, CONTAINER_PROPERTIES),
                  isActive && p(`${themeKey}Active`, CONTAINER_PROPERTIES),
                ),
            )}
          >
            {icon}
            <Text
              value={value}
              lines={lines}
              style={combineStyles(
                // Base
                p('chip', TEXT_PROPERTIES),
                isHover && p('chipHover', TEXT_PROPERTIES),
                isActive && p('chipActive', TEXT_PROPERTIES),

                !!themeKey &&
                  combineStyles(
                    p(themeKey, TEXT_PROPERTIES),
                    isHover && p(`${themeKey}Hover`, TEXT_PROPERTIES),
                    isActive && p(`${themeKey}Active`, TEXT_PROPERTIES),
                  ),
              )}
              variant={'body2'}
            />
          </div>
        )}
      </Pseudo>
    </Show>
  );
};

export default Chip;

const styles: Styles = {
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    minHeight: 32,
    borderRadius: 16,
    borderWidth: 2,
    padding: '3px 12px',
    gap: 4,
  },
};
